import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { 
  Truck, 
  ArrowLeft,
  Plus,
  Trash2,
  CalendarIcon,
  Package,
  User,
  MapPin,
  Phone,
  FileText,
  AlertTriangle,
  LogOut,
  CreditCard,
  CheckCircle2,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import { format, addDays, isWeekend, isSaturday } from "date-fns";
import { de } from "date-fns/locale";

export default function CreateOrder() {
  const navigate = useNavigate();
  const { user, token, logout } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentStep, setPaymentStep] = useState("select"); // select, processing, success
  const [pendingOrderData, setPendingOrderData] = useState(null);
  
  // Redirect inactive users
  useEffect(() => {
    if (user && user.is_active === false) {
      toast.error("Ihr Konto wartet noch auf Aktivierung. Sie können noch keine Sendungen erstellen.");
      navigate("/dashboard");
    }
  }, [user, navigate]);
  
  // Pricing settings from backend
  const [pricing, setPricing] = useState({
    base_price: 29.90,
    express_12_surcharge: 10.00,
    express_10_surcharge: 20.00,
    saturday_surcharge: 20.00,
    volume_surcharge_per_m3: 15.00,
    volume_threshold_m3: 0.1
  });
  
  // Form state
  const [sender, setSender] = useState({
    name: user?.name || "",
    address: user?.address || "",
    postal_code: user?.postal_code || "",
    city: user?.city || "",
    phone: user?.phone || "",
    notes: ""
  });
  
  const [receiver, setReceiver] = useState({
    name: "",
    address: "",
    postal_code: "",
    city: "",
    phone: "",
    notes: ""
  });
  
  const [pickupDate, setPickupDate] = useState(addDays(new Date(), 1));
  const [deliveryDate, setDeliveryDate] = useState(addDays(new Date(), 2));
  const [pickupTimeWindow, setPickupTimeWindow] = useState("08:00 - 17:00");
  const [deliveryTimeWindow, setDeliveryTimeWindow] = useState("08:00 - 17:00");
  const [showTodayWarning, setShowTodayWarning] = useState(false);
  
  const [deliveryBy12, setDeliveryBy12] = useState(false);
  const [deliveryBy10, setDeliveryBy10] = useState(false);
  const [saturdayDelivery, setSaturdayDelivery] = useState(false);
  const [destinationCountry, setDestinationCountry] = useState("DE");
  
  const [packages, setPackages] = useState([
    { id: (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, category: "STANDARD", weight: 1.0, length: 0, width: 0, height: 0 }
  ]);

  // Package categories
  const PACKAGE_CATEGORIES = [
    { value: "STANDARD", label: "Standard" },
    { value: "VALOREN", label: "Valoren (Wertgegenstände)" },
    { value: "FRAGILE", label: "Zerbrechlich" },
    { value: "DOKUMENTE", label: "Dokumente" },
    { value: "WAFFEN", label: "Jagd & Sportwaffen" },
    { value: "BLUTPROBEN", label: "Blutproben / Medizinische Proben" }
  ];

  const headers = { Authorization: `Bearer ${token}` };

  // Load pricing settings on mount
  useEffect(() => {
    const loadPricing = async () => {
      try {
        const res = await axios.get(`${API}/settings/pricing`, { headers });
        setPricing(res.data);
      } catch (e) {
        console.log("Using default pricing");
      }
    };
    loadPricing();
  }, []);

  // Auto-adjust delivery date when pickup date changes
  useEffect(() => {
    let delivery = addDays(pickupDate, 1);
    // Skip Sunday
    if (delivery.getDay() === 0) {
      delivery = addDays(delivery, 1);
    }
    // If Saturday delivery not enabled and it falls on Saturday
    if (!saturdayDelivery && delivery.getDay() === 6) {
      delivery = addDays(delivery, 2);
    }
    setDeliveryDate(delivery);
  }, [pickupDate, saturdayDelivery]);

  // Update delivery time window based on options
  useEffect(() => {
    if (deliveryBy10) {
      setDeliveryTimeWindow("08:00 - 10:00");
    } else if (deliveryBy12) {
      setDeliveryTimeWindow("08:00 - 12:00");
    } else {
      setDeliveryTimeWindow("08:00 - 17:00");
    }
  }, [deliveryBy10, deliveryBy12]);

  const addPackage = () => {
    setPackages([...packages, { id: (window.crypto && window.crypto.randomUUID && window.crypto.randomUUID()) || `${Date.now()}-${Math.random().toString(36).slice(2, 10)}`, category: "STANDARD", weight: 1.0, length: 0, width: 0, height: 0 }]);
  };

  const removePackage = (id) => {
    if (packages.length > 1) {
      setPackages(packages.filter(p => p.id !== id));
    }
  };

  const updatePackage = (id, field, value) => {
    setPackages(packages.map(p => p.id === id ? { ...p, [field]: value } : p));
  };

  // Calculate total volume in m³
  const calculateVolume = () => {
    let totalVolume = 0;
    packages.forEach(pkg => {
      const l = pkg.length || 0;
      const w = pkg.width || 0;
      const h = pkg.height || 0;
      if (l > 0 && w > 0 && h > 0) {
        totalVolume += (l * w * h) / 1000000; // cm³ to m³
      }
    });
    return totalVolume;
  };

  // Calculate total weight
  const calculateTotalWeight = () => {
    return packages.reduce((sum, pkg) => sum + (pkg.weight || 1.0), 0);
  };

  // Get country-specific pricing value
  const getCountryVal = (field) => {
    if (destinationCountry === "AT") {
      return pricing["austria_" + field] ?? pricing[field] ?? 0;
    }
    return pricing[field] ?? 0;
  };

  // Calculate weight surcharge (cumulative)
  const calculateWeightSurcharge = () => {
    const totalWeight = calculateTotalWeight();
    const threshold = pricing.weight_threshold_kg || 10;
    const baseSurcharge = getCountryVal("weight_surcharge") || 5;
    const stepKg = pricing.weight_step_kg || 5;
    const stepSurcharge = getCountryVal("weight_step_surcharge") || 5;
    
    if (totalWeight < threshold) return 0;
    
    let surcharge = baseSurcharge;
    const excessWeight = totalWeight - threshold;
    if (excessWeight > 0 && stepKg > 0) {
      const steps = Math.floor(excessWeight / stepKg);
      surcharge += steps * stepSurcharge;
    }
    return surcharge;
  };

  // Calculate volume surcharge
  const calculateVolumeSurcharge = () => {
    const totalVolume = calculateVolume();
    if (totalVolume >= pricing.volume_threshold_m3) {
      return totalVolume * getCountryVal("volume_surcharge_per_m3");
    }
    return 0;
  };

  const calculatePriceBreakdown = () => {
    // Calculate package cost based on category
    let packageCost = 0;
    const packagePrices = [];
    const isAT = destinationCountry === "AT";
    const categoryPrices = isAT
      ? (pricing.austria_category_prices || {})
      : (pricing.category_prices || {});
    const customerCategoryRates = user?.category_flat_rates || {};
    const legacyFlatRate = user?.flat_rate;
    
    packages.forEach(pkg => {
      const category = pkg.category || "STANDARD";
      let pkgPrice;
      
      // Priority: Customer category rate > Legacy flat rate > Global category price > Default
      if (customerCategoryRates[category]) {
        pkgPrice = parseFloat(customerCategoryRates[category]);
      } else if (legacyFlatRate) {
        pkgPrice = parseFloat(legacyFlatRate);
      } else if (categoryPrices[category]) {
        pkgPrice = parseFloat(categoryPrices[category]);
      } else {
        pkgPrice = pricing.base_price;
      }
      
      packagePrices.push(pkgPrice);
    });
      if (packagePrices.length > 0) {
      const highestPackagePrice = Math.max(...packagePrices);
      const additionalCount = Math.max(0, packagePrices.length - 1);
      packageCost = highestPackagePrice + (additionalCount * 5);
      }
    
    // Surcharges
    let expressSurcharge = 0;
    if (deliveryBy10) {
      expressSurcharge = getCountryVal("express_10_surcharge");
    } else if (deliveryBy12) {
      expressSurcharge = getCountryVal("express_12_surcharge");
    }
    
    const saturdaySurcharge = saturdayDelivery ? getCountryVal("saturday_surcharge") : 0;
    const volumeSurcharge = calculateVolumeSurcharge();
    const weightSurcharge = calculateWeightSurcharge();
    
    // Pickup time window surcharge
    let pickupTimeSurcharge = 0;
    if (pickupTimeWindow === "08:00 - 12:00") {
      pickupTimeSurcharge = getCountryVal("pickup_morning_surcharge") || 0;
    } else if (pickupTimeWindow === "12:00 - 17:00") {
      pickupTimeSurcharge = getCountryVal("pickup_afternoon_surcharge") || 0;
    }
    
    const netTotal = packageCost + expressSurcharge + saturdaySurcharge + volumeSurcharge + weightSurcharge + pickupTimeSurcharge;
    const vatAmount = netTotal * 0.19;
    const grossTotal = netTotal + vatAmount;
    
    return {
      packageCost: Math.round(packageCost * 100) / 100,
      expressSurcharge: Math.round(expressSurcharge * 100) / 100,
      saturdaySurcharge: Math.round(saturdaySurcharge * 100) / 100,
      volumeSurcharge: Math.round(volumeSurcharge * 100) / 100,
      weightSurcharge: Math.round(weightSurcharge * 100) / 100,
      pickupTimeSurcharge: Math.round(pickupTimeSurcharge * 100) / 100,
      netTotal: Math.round(netTotal * 100) / 100,
      vatAmount: Math.round(vatAmount * 100) / 100,
      grossTotal: Math.round(grossTotal * 100) / 100,
      hasSurcharges: expressSurcharge > 0 || saturdaySurcharge > 0 || volumeSurcharge > 0 || weightSurcharge > 0 || pickupTimeSurcharge > 0
    };
  };

  const calculatePrice = () => {
    return calculatePriceBreakdown().netTotal;
  };

  const handleSubmit = async () => {
    // Validation
    if (!sender.name || !sender.address || !sender.postal_code || !sender.city || !sender.phone) {
      toast.error("Bitte alle Absender-Pflichtfelder ausfüllen");
      return;
    }
    
    if (!receiver.name || !receiver.address || !receiver.postal_code || !receiver.city || !receiver.phone) {
      toast.error("Bitte alle Empfänger-Pflichtfelder ausfüllen");
      return;
    }
    
    const orderData = {
      sender_name: sender.name,
      sender_address: sender.address,
      sender_postal_code: sender.postal_code,
      sender_city: sender.city,
      sender_phone: sender.phone,
      sender_notes: sender.notes,
      receiver_name: receiver.name,
      receiver_address: receiver.address,
      receiver_postal_code: receiver.postal_code,
      receiver_city: receiver.city,
      receiver_phone: receiver.phone,
      receiver_notes: receiver.notes,
      pickup_date: format(pickupDate, "yyyy-MM-dd"),
      delivery_date: format(deliveryDate, "yyyy-MM-dd"),
      pickup_time_window: pickupTimeWindow,
      delivery_time_window: deliveryTimeWindow,
      packages: packages.map(p => ({
        category: p.category,
        weight: p.weight,
        length: p.length || 0,
        width: p.width || 0,
        height: p.height || 0
      })),
      delivery_by_12: deliveryBy12,
      delivery_by_10: deliveryBy10,
      saturday_delivery: saturdayDelivery,
      destination_country: destinationCountry
    };
    
    // Check if user needs to pay (immediate payment) or can order on invoice
    if (user?.payment_method === "invoice") {
      // Business customer with invoice - create order directly
      await createOrder(orderData);
    } else {
      // Private customer - show payment modal
      setPendingOrderData(orderData);
      setShowPaymentModal(true);
      setPaymentStep("select");
    }
  };

  const createOrder = async (orderData) => {
    setLoading(true);
    try {
      const response = await axios.post(`${API}/orders`, orderData, { headers });
      toast.success("Auftrag erfolgreich erstellt!");
      navigate(`/order/${response.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Fehler beim Erstellen des Auftrags");
    } finally {
      setLoading(false);
    }
  };

  const handlePayPalPayment = async () => {
    setPaymentStep("processing");
    
    // Simulate PayPal payment process
    await new Promise(resolve => setTimeout(resolve, 2000));
    
    setPaymentStep("success");
    
    // Wait for success animation, then create order
    await new Promise(resolve => setTimeout(resolve, 1500));
    
    setShowPaymentModal(false);
    await createOrder(pendingOrderData);
  };

  const isPickupDateToday = pickupDate.toDateString() === new Date().toDateString();

  const handlePickupDateChange = (date) => {
    if (!date) return;
    
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    
    if (date.toDateString() === today.toDateString()) {
      setShowTodayWarning(true);
    } else {
      setShowTodayWarning(false);
    }
    
    setPickupDate(date);
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center">
                <Truck className="h-6 w-6 text-white" />
              </div>
              <div>
                <span className="font-bold text-lg">SonderCargo.de</span>
                <span className="text-xs text-slate-400 block">Medical, Weapons and much more</span>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <div className="text-right hidden sm:block">
                <p className="font-medium">{user?.name}</p>
                <p className="text-xs text-slate-400 uppercase">{user?.role}</p>
              </div>
              <Button variant="ghost" size="icon" className="text-slate-300 hover:text-white" onClick={logout} data-testid="logout-btn">
                <LogOut className="h-5 w-5" />
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="h-1 bg-gradient-to-r from-blue-600 via-blue-500 to-blue-400" />

      {/* Main Content */}
      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Link 
          to={user?.role === "admin" || user?.role === "support" ? "/admin" : "/dashboard"} 
          className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6"
          data-testid="back-link"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Zurück zum Dashboard
        </Link>

        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900" data-testid="page-title">Neue Sendung erstellen</h1>
            <p className="text-slate-500">Express-Versand beauftragen</p>
          </div>
          <div className="text-right">
            {(() => {
              const breakdown = calculatePriceBreakdown();
              return (
                <>
                  <p className="text-sm text-slate-500">Geschätzter Preis (Netto)</p>
                  <p className="text-3xl font-bold text-blue-600" data-testid="estimated-price">{breakdown.netTotal.toFixed(2)}€</p>
                  <p className="text-sm text-slate-400">inkl. 19% MwSt.: <span className="font-semibold text-slate-600">{breakdown.grossTotal.toFixed(2)}€</span></p>
                </>
              );
            })()}
          </div>
        </div>

        {/* Warning for same-day pickup */}
        {(isPickupDateToday || showTodayWarning) && (
          <div className="mb-6 p-4 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-3" data-testid="warning-today">
            <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-medium text-amber-800">Achtung: Abholung heute ausgewählt</p>
              <p className="text-sm text-amber-700">
                Eine Abholung am selben Tag kann <strong>nicht garantiert</strong> werden. 
                Bitte stellen Sie sicher, dass die Sendung sofort bereit ist und kontaktieren Sie uns telefonisch zur Bestätigung.
              </p>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-2 gap-6 mb-8">
          {/* Destination Country Selector */}
          <div className="lg:col-span-2">
            <Card className="border-0 shadow-sm" data-testid="destination-country-card">
              <CardContent className="p-4">
                <div className="flex items-center gap-4">
                  <Label className="font-semibold text-slate-700 whitespace-nowrap">Zielland:</Label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setDestinationCountry("DE")}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all text-sm font-medium ${
                        destinationCountry === "DE"
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid="country-de"
                    >
                      <span className="text-lg">&#127465;&#127466;</span>
                      Deutschland
                    </button>
                    <button
                      type="button"
                      onClick={() => setDestinationCountry("AT")}
                      className={`flex items-center gap-2 px-4 py-2 rounded-lg border-2 transition-all text-sm font-medium ${
                        destinationCountry === "AT"
                          ? "border-blue-500 bg-blue-50 text-blue-700"
                          : "border-slate-200 bg-white text-slate-600 hover:border-slate-300"
                      }`}
                      data-testid="country-at"
                    >
                      <span className="text-lg">&#127462;&#127481;</span>
                      Österreich
                    </button>
                  </div>
                  {destinationCountry === "AT" && (
                    <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium">Internationaler Versand</span>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sender Card */}
          <Card className="border-0 shadow-sm" data-testid="sender-card">
            <CardHeader className="bg-blue-50 rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-blue-900">
                <User className="h-5 w-5" />
                Absender-Details
                <span className="text-xs bg-blue-200 text-blue-800 px-2 py-1 rounded ml-auto">ABSENDER-ZENTRALE</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Name / Firma *</Label>
                <Input 
                  value={sender.name} 
                  onChange={(e) => setSender({ ...sender, name: e.target.value })}
                  placeholder="Max Mustermann"
                  data-testid="sender-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Adresse *</Label>
                <Input 
                  value={sender.address} 
                  onChange={(e) => setSender({ ...sender, address: e.target.value })}
                  placeholder="Musterstraße 1"
                  data-testid="sender-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>PLZ *</Label>
                  <Input 
                    value={sender.postal_code} 
                    onChange={(e) => setSender({ ...sender, postal_code: e.target.value })}
                    placeholder="12345"
                    data-testid="sender-postal"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stadt *</Label>
                  <Input 
                    value={sender.city} 
                    onChange={(e) => setSender({ ...sender, city: e.target.value })}
                    placeholder="Berlin"
                    data-testid="sender-city"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Telefon *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input 
                    value={sender.phone} 
                    onChange={(e) => setSender({ ...sender, phone: e.target.value })}
                    placeholder="030 123456"
                    className="pl-10"
                    data-testid="sender-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Spezielle Anweisung</Label>
                <Textarea 
                  value={sender.notes} 
                  onChange={(e) => setSender({ ...sender, notes: e.target.value })}
                  placeholder="Hinweise zur Abholung..."
                  className="bg-amber-50 border-amber-200"
                  data-testid="sender-notes"
                />
              </div>
            </CardContent>
          </Card>

          {/* Receiver Card */}
          <Card className="border-0 shadow-sm" data-testid="receiver-card">
            <CardHeader className="bg-indigo-50 rounded-t-lg">
              <CardTitle className="flex items-center gap-2 text-indigo-900">
                <MapPin className="h-5 w-5" />
                Empfänger-Details
                <span className="text-xs bg-indigo-200 text-indigo-800 px-2 py-1 rounded ml-auto">ZIEL-EMPFÄNGER</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-6 space-y-4">
              <div className="space-y-2">
                <Label>Name / Firma *</Label>
                <Input 
                  value={receiver.name} 
                  onChange={(e) => setReceiver({ ...receiver, name: e.target.value })}
                  placeholder="Empfänger Name"
                  data-testid="receiver-name"
                />
              </div>
              <div className="space-y-2">
                <Label>Adresse *</Label>
                <Input 
                  value={receiver.address} 
                  onChange={(e) => setReceiver({ ...receiver, address: e.target.value })}
                  placeholder="Zielstraße 10"
                  data-testid="receiver-address"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>PLZ *</Label>
                  <Input 
                    value={receiver.postal_code} 
                    onChange={(e) => setReceiver({ ...receiver, postal_code: e.target.value })}
                    placeholder="54321"
                    data-testid="receiver-postal"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stadt *</Label>
                  <Input 
                    value={receiver.city} 
                    onChange={(e) => setReceiver({ ...receiver, city: e.target.value })}
                    placeholder="München"
                    data-testid="receiver-city"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Telefon *</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input 
                    value={receiver.phone} 
                    onChange={(e) => setReceiver({ ...receiver, phone: e.target.value })}
                    placeholder="089 987654"
                    className="pl-10"
                    data-testid="receiver-phone"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Zustell-Notiz</Label>
                <Textarea 
                  value={receiver.notes} 
                  onChange={(e) => setReceiver({ ...receiver, notes: e.target.value })}
                  placeholder="Hinweise zur Zustellung..."
                  className="bg-indigo-50 border-indigo-200"
                  data-testid="receiver-notes"
                />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Dates */}
        <Card className="border-0 shadow-sm mb-8" data-testid="dates-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5" />
              Abhol- und Zustelltermine
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Abholung am</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal" data-testid="pickup-date-btn">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(pickupDate, "dd.MM.yyyy", { locale: de })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={pickupDate}
                        onSelect={handlePickupDateChange}
                        disabled={(date) => {
                          const today = new Date();
                          today.setHours(0, 0, 0, 0);
                          const yesterday = new Date(today);
                          yesterday.setDate(yesterday.getDate() - 1);
                          return date < yesterday || date.getDay() === 0;
                        }}
                        locale={de}
                      />
                      <div className="p-3 border-t">
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="w-full text-amber-700 border-amber-300 hover:bg-amber-50"
                          onClick={() => handlePickupDateChange(new Date())}
                        >
                          <AlertTriangle className="h-4 w-4 mr-2" />
                          Heute auswählen (nicht garantiert)
                        </Button>
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Zeitfenster</Label>
                  <Select value={pickupTimeWindow} onValueChange={setPickupTimeWindow}>
                    <SelectTrigger data-testid="pickup-time-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="08:00 - 12:00">08:00 - 12:00 Uhr</SelectItem>
                      <SelectItem value="12:00 - 17:00">12:00 - 17:00 Uhr</SelectItem>
                      <SelectItem value="08:00 - 17:00">08:00 - 17:00 Uhr</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Zustellung am</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal bg-indigo-50" data-testid="delivery-date-btn">
                        <CalendarIcon className="mr-2 h-4 w-4 text-indigo-600" />
                        {format(deliveryDate, "dd.MM.yyyy", { locale: de })}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={deliveryDate}
                        onSelect={(date) => {
                          if (date) {
                            // Ensure delivery date is after pickup date
                            if (date <= pickupDate) {
                              toast.error("Lieferdatum muss nach Abholdatum sein");
                              return;
                            }
                            // Check Saturday - only allow if saturdayDelivery is enabled
                            if (date.getDay() === 6 && !saturdayDelivery) {
                              toast.error("Samstagszustellung muss aktiviert sein für Samstag");
                              return;
                            }
                            // No Sunday delivery
                            if (date.getDay() === 0) {
                              toast.error("Sonntags keine Zustellung möglich");
                              return;
                            }
                            setDeliveryDate(date);
                          }
                        }}
                        disabled={(date) => {
                          // Disable past dates and pickup date
                          const minDate = addDays(pickupDate, 1);
                          if (date < minDate) return true;
                          // Disable Sundays
                          if (date.getDay() === 0) return true;
                          // Disable Saturdays if saturdayDelivery not enabled
                          if (date.getDay() === 6 && !saturdayDelivery) return true;
                          return false;
                        }}
                        locale={de}
                      />
                    </PopoverContent>
                  </Popover>
                  <p className="text-xs text-slate-500">
                    Standard: nächster Werktag nach Abholung
                    {saturdayDelivery && " (Samstag aktiviert)"}
                  </p>
                </div>
                <div className="space-y-2">
                  <Label>Zeitfenster</Label>
                  <Input value={deliveryTimeWindow} disabled className="bg-slate-50" data-testid="delivery-time-display" />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Options */}
        <Card className="border-0 shadow-sm mb-8" data-testid="options-card">
          <CardHeader>
            <CardTitle>Zustelloptionen</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-slate-50">
                <Checkbox 
                  id="delivery12" 
                  checked={deliveryBy12}
                  onCheckedChange={(v) => {
                    setDeliveryBy12(v);
                    if (v) setDeliveryBy10(false);
                  }}
                  data-testid="option-delivery12"
                />
                <div>
                  <label htmlFor="delivery12" className="font-medium cursor-pointer">Zustellung bis 12 Uhr</label>
                  <p className="text-sm text-green-600 font-medium">+{getCountryVal("express_12_surcharge").toFixed(2)}€</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-slate-50">
                <Checkbox 
                  id="delivery10" 
                  checked={deliveryBy10}
                  onCheckedChange={(v) => {
                    setDeliveryBy10(v);
                    if (v) setDeliveryBy12(false);
                  }}
                  data-testid="option-delivery10"
                />
                <div>
                  <label htmlFor="delivery10" className="font-medium cursor-pointer">Zustellung bis 10 Uhr</label>
                  <p className="text-sm text-amber-600 font-medium">+{getCountryVal("express_10_surcharge").toFixed(2)}€</p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3 p-4 border rounded-lg hover:bg-slate-50">
                <Checkbox 
                  id="saturday" 
                  checked={saturdayDelivery}
                  onCheckedChange={setSaturdayDelivery}
                  data-testid="option-saturday"
                />
                <div>
                  <label htmlFor="saturday" className="font-medium cursor-pointer">Samstag Zustellung</label>
                  <p className="text-sm text-purple-600 font-medium">+{getCountryVal("saturday_surcharge").toFixed(2)}€</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Packages */}
        <Card className="border-0 shadow-sm mb-8" data-testid="packages-card">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Package className="h-5 w-5" />
              Packstücke ({packages.length})
            </CardTitle>
            <Button variant="outline" onClick={addPackage} data-testid="add-package-btn">
              <Plus className="h-4 w-4 mr-2" />
              Packstück hinzufügen
            </Button>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {packages.map((pkg, idx) => (
                <div key={pkg.id} className="p-4 border rounded-lg" data-testid={`package-${idx}`}>
                  <div className="flex items-center gap-4 mb-4">
                    <span className="text-lg font-bold text-slate-400 w-8">{idx + 1}</span>
                    <div className="flex-1 grid md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Kategorie</Label>
                        <Select value={pkg.category} onValueChange={(v) => updatePackage(pkg.id, "category", v)}>
                          <SelectTrigger data-testid={`package-category-${idx}`}>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {PACKAGE_CATEGORIES.map(cat => (
                              <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label>Gewicht (kg)</Label>
                        <Input 
                          type="number"
                          step="0.1"
                          min="0.1"
                          value={pkg.weight}
                          onChange={(e) => updatePackage(pkg.id, "weight", parseFloat(e.target.value) || 0)}
                          data-testid={`package-weight-${idx}`}
                        />
                      </div>
                    </div>
                    {packages.length > 1 && (
                      <Button 
                        variant="ghost" 
                        size="icon"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => removePackage(pkg.id)}
                        data-testid={`remove-package-${idx}`}
                      >
                        <Trash2 className="h-5 w-5" />
                      </Button>
                    )}
                  </div>
                  {/* Dimensions for volume calculation */}
                  <div className="ml-12 grid grid-cols-3 gap-3">
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Länge (cm)</Label>
                      <Input 
                        type="number"
                        step="1"
                        min="0"
                        placeholder="0"
                        value={pkg.length || ""}
                        onChange={(e) => updatePackage(pkg.id, "length", parseFloat(e.target.value) || 0)}
                        className="h-8 text-sm"
                        data-testid={`package-length-${idx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Breite (cm)</Label>
                      <Input 
                        type="number"
                        step="1"
                        min="0"
                        placeholder="0"
                        value={pkg.width || ""}
                        onChange={(e) => updatePackage(pkg.id, "width", parseFloat(e.target.value) || 0)}
                        className="h-8 text-sm"
                        data-testid={`package-width-${idx}`}
                      />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs text-slate-500">Höhe (cm)</Label>
                      <Input 
                        type="number"
                        step="1"
                        min="0"
                        placeholder="0"
                        value={pkg.height || ""}
                        onChange={(e) => updatePackage(pkg.id, "height", parseFloat(e.target.value) || 0)}
                        className="h-8 text-sm"
                        data-testid={`package-height-${idx}`}
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {calculateVolume() > pricing.volume_threshold_m3 && (
              <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                <p className="text-sm text-amber-800">
                  <strong>Volumen-Zuschlag:</strong> Gesamtvolumen {calculateVolume().toFixed(3)} m³ × {pricing.volume_surcharge_per_m3.toFixed(2)}€/m³ = +{calculateVolumeSurcharge().toFixed(2)}€
                </p>
              </div>
            )}
            {calculateWeightSurcharge() > 0 && (
              <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                <p className="text-sm text-orange-800">
                  <strong>Gewichts-Zuschlag:</strong> Gesamtgewicht {calculateTotalWeight().toFixed(1)} kg (ab {pricing.weight_threshold_kg}kg) = +{calculateWeightSurcharge().toFixed(2)}€
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Summary & Submit */}
        <Card className="border-0 shadow-sm bg-slate-900 text-white" data-testid="summary-card">
          <CardContent className="p-6">
            {(() => {
              const breakdown = calculatePriceBreakdown();
              return (
                <>
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl font-semibold">Zusammenfassung</h3>
                      <p className="text-slate-400 mt-1">
                        {packages.length} Packstück(e) • 
                        Abholung: {format(pickupDate, "dd.MM.yyyy")} • 
                        Zustellung: {format(deliveryDate, "dd.MM.yyyy")}
                      </p>
                      {user?.payment_method === "invoice" && (
                        <p className="text-green-400 text-sm mt-2">✓ Zahlung auf Rechnung</p>
                      )}
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-400">Gesamtpreis (Netto)</p>
                      <p className="text-4xl font-bold">{breakdown.netTotal.toFixed(2)}€</p>
                      <p className="text-sm text-slate-400 mt-1">
                        inkl. 19% MwSt.: <span className="text-white font-semibold">{breakdown.grossTotal.toFixed(2)}€</span>
                      </p>
                    </div>
                  </div>
                  
                  {/* Price breakdown */}
                  {breakdown.hasSurcharges && (
                    <div className="mt-4 pt-4 border-t border-slate-700">
                      <p className="text-sm text-slate-400 mb-2">Preisaufschlüsselung:</p>
                      <div className="grid grid-cols-2 gap-2 text-sm">
                        <span className="text-slate-400">Grundpreis ({packages.length}× {(user?.flat_rate || pricing.base_price).toFixed(2)}€):</span>
                        <span className="text-right">{breakdown.packageCost.toFixed(2)}€</span>
                        
                        {breakdown.expressSurcharge > 0 && (
                          <>
                            <span className="text-slate-400">Express-Zuschlag:</span>
                            <span className="text-right text-green-400">+{breakdown.expressSurcharge.toFixed(2)}€</span>
                          </>
                        )}
                        
                        {breakdown.saturdaySurcharge > 0 && (
                          <>
                            <span className="text-slate-400">Samstags-Zuschlag:</span>
                            <span className="text-right text-purple-400">+{breakdown.saturdaySurcharge.toFixed(2)}€</span>
                          </>
                        )}
                        
                        {breakdown.volumeSurcharge > 0 && (
                          <>
                            <span className="text-slate-400">Volumen-Zuschlag:</span>
                            <span className="text-right text-amber-400">+{breakdown.volumeSurcharge.toFixed(2)}€</span>
                          </>
                        )}
                        
                        {breakdown.weightSurcharge > 0 && (
                          <>
                            <span className="text-slate-400">Gewichts-Zuschlag:</span>
                            <span className="text-right text-orange-400">+{breakdown.weightSurcharge.toFixed(2)}€</span>
                          </>
                        )}
                        
                        {breakdown.pickupTimeSurcharge > 0 && (
                          <>
                            <span className="text-slate-400">Zeitfenster-Zuschlag:</span>
                            <span className="text-right text-cyan-400">+{breakdown.pickupTimeSurcharge.toFixed(2)}€</span>
                          </>
                        )}
                        
                        <span className="text-slate-300 font-medium border-t border-slate-700 pt-2">Netto:</span>
                        <span className="text-right font-medium border-t border-slate-700 pt-2">{breakdown.netTotal.toFixed(2)}€</span>
                        
                        <span className="text-slate-400">MwSt. (19%):</span>
                        <span className="text-right text-slate-400">+{breakdown.vatAmount.toFixed(2)}€</span>
                        
                        <span className="text-white font-bold">Brutto:</span>
                        <span className="text-right text-white font-bold">{breakdown.grossTotal.toFixed(2)}€</span>
                      </div>
                    </div>
                  )}
                </>
              );
            })()}
            <div className="mt-6 flex justify-end gap-4">
              <Button 
                variant="outline" 
                className="text-white border-slate-700 hover:bg-slate-800"
                onClick={() => navigate(-1)}
                data-testid="cancel-btn"
              >
                Abbrechen
              </Button>
              <Button 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={handleSubmit}
                disabled={loading}
                data-testid="submit-order-btn"
              >
                {loading ? (
                  <span className="flex items-center gap-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Erstellen...
                  </span>
                ) : user?.payment_method === "invoice" ? (
                  "Auftrag erstellen"
                ) : (
                  <>
                    <CreditCard className="h-4 w-4 mr-2" />
                    Zur Zahlung
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* PayPal Payment Modal */}
      <Dialog open={showPaymentModal} onOpenChange={(open) => {
        if (!open && paymentStep !== "processing") {
          setShowPaymentModal(false);
          setPaymentStep("select");
        }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="payment-modal">
          <DialogHeader>
            <DialogTitle className="text-center">
              {paymentStep === "select" && "Zahlung"}
              {paymentStep === "processing" && "Zahlung wird verarbeitet..."}
              {paymentStep === "success" && "Zahlung erfolgreich!"}
            </DialogTitle>
          </DialogHeader>
          
          {paymentStep === "select" && (
            <div className="space-y-4">
              {(() => {
                const breakdown = calculatePriceBreakdown();
                return (
                  <div className="text-center py-4">
                    <p className="text-3xl font-bold text-slate-900">{breakdown.netTotal.toFixed(2)}€ <span className="text-base font-normal text-slate-500">Netto</span></p>
                    <p className="text-lg text-slate-600 mt-1">inkl. MwSt.: <span className="font-semibold">{breakdown.grossTotal.toFixed(2)}€</span></p>
                    <p className="text-xs text-slate-400 mt-1">Gesamtbetrag (Brutto)</p>
                  </div>
                );
              })()}
              
              <div className="border rounded-lg p-4 hover:bg-slate-50 transition cursor-pointer" onClick={handlePayPalPayment} data-testid="paypal-option">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-10 bg-[#003087] rounded flex items-center justify-center">
                    <span className="text-white font-bold text-sm">PayPal</span>
                  </div>
                  <div className="flex-1">
                    <p className="font-medium">Mit PayPal bezahlen</p>
                    <p className="text-sm text-slate-500">Schnell & sicher</p>
                  </div>
                </div>
              </div>
              
              <div className="border rounded-lg p-4 hover:bg-slate-50 transition cursor-pointer opacity-50">
                <div className="flex items-center gap-4">
                  <div className="w-16 h-10 bg-slate-200 rounded flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-slate-500" />
                  </div>
                  <div className="flex-1">
                    <p className="font-medium text-slate-400">Kreditkarte</p>
                    <p className="text-sm text-slate-400">Demnächst verfügbar</p>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {paymentStep === "processing" && (
            <div className="py-12 text-center">
              <div className="w-16 h-16 bg-[#003087] rounded-full flex items-center justify-center mx-auto mb-4 animate-pulse">
                <span className="text-white font-bold">PayPal</span>
              </div>
              <Loader2 className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-4" />
              <p className="text-slate-600">Verbindung zu PayPal wird hergestellt...</p>
              <p className="text-sm text-slate-400 mt-2">Bitte warten Sie einen Moment</p>
            </div>
          )}
          
          {paymentStep === "success" && (
            <div className="py-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle2 className="h-10 w-10 text-green-600" />
              </div>
              <p className="text-xl font-semibold text-green-600">Zahlung erfolgreich!</p>
              <p className="text-sm text-slate-500 mt-2">Ihr Auftrag wird jetzt erstellt...</p>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
